import React, { useState, useMemo } from 'react';
import { aiService } from '../../services/aiService';
import { storageService } from '../../services/storageService';
import { Policy, PolicySection, PolicyVersion, SoAControl } from '../../types/iso27001';
import { GRCControl, Framework } from '../../types/grc';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Plus, Download, Save, ArrowLeft, History, RotateCcw, FileText, RefreshCw, Loader2, Eye, CheckCircle, XCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import { Modal } from '../ui/Modal';

interface SortableItemProps {
  id: string;
  section: PolicySection;
  onUpdate: (id: string, field: keyof PolicySection, value: string) => void;
  onDelete: (id: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, section, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-3">
      <div className="flex items-start gap-3">
        <div {...attributes} {...listeners} className="mt-2 cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
          <GripVertical className="w-5 h-5" />
        </div>
        <div className="flex-1 space-y-3">
          <input
            type="text"
            value={section.title}
            onChange={(e) => onUpdate(id, 'title', e.target.value)}
            className="block w-full font-semibold text-gray-800 dark:text-gray-100 bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-indigo-500 focus:outline-none px-1 py-1"
            placeholder="Section Title"
          />
          <textarea
            value={section.content}
            onChange={(e) => onUpdate(id, 'content', e.target.value)}
            className="block w-full text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md p-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            rows={4}
            placeholder="Section Content"
          />
        </div>
        <button onClick={() => onDelete(id)} className="text-red-400 hover:text-red-600 p-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

interface PolicyEditorProps {
  policy: Policy;
  onSave: (policy: Policy) => void;
  onCancel: () => void;
  onNavigate?: (tab: string, filters?: any) => void;
  framework?: Framework | null;
  frameworkControls?: GRCControl[];
}

export const PolicyEditor: React.FC<PolicyEditorProps> = ({ policy, onSave, onCancel, onNavigate, framework, frameworkControls }) => {
  const [sections, setSections] = useState<PolicySection[]>(policy.sections || []);
  const [title, setTitle] = useState(policy.title);
  const [owner, setOwner] = useState(policy.owner || '');
  const [approver, setApprover] = useState(policy.approver || '');
  const [nextReviewDate, setNextReviewDate] = useState(policy.nextReviewDate || '');
  const [coveredControls, setCoveredControls] = useState<string[]>(policy.coveredControls || []);
  const [activeTab, setActiveTab] = useState<'editor' | 'history'>('editor');
  const [analyzing, setAnalyzing] = useState(false);
  
  // New State for Modals
  const [viewVersion, setViewVersion] = useState<PolicyVersion | null>(null);
  const [showControlsModal, setShowControlsModal] = useState(false);
  const [selectedControlId, setSelectedControlId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Use framework controls if available, otherwise fallback to localStorage SoA
  const soaControls = useMemo(() => {
    if (frameworkControls && frameworkControls.length > 0) {
      // Map GRCControl to SoAControl format
      return frameworkControls.map(c => ({
        id: c.control_code || `ctrl-${c.id}`,
        name: c.title,
        description: c.description || '',
        applicable: c.applicable === 1,
        justification: c.applicability_justification || '',
        implementationStatus: c.status === 'compliant' ? 'implemented' as const : c.status === 'partial' ? 'in_progress' as const : 'not_started' as const,
        owner: c.owner || '',
        fulfillmentRationale: c.fulfillment_rationale || '',
        iso27002Guidance: c.implementation_notes || '',
        coveredByPolicies: c.suggested_policy_names || [],
        implementationSteps: c.implementation_steps || [],
      }));
    }
    return storageService.getSoA();
  }, [frameworkControls]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const updateSection = (id: string, field: keyof PolicySection, value: string) => {
    setSections(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const deleteSection = (id: string) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const addSection = () => {
    const newSection: PolicySection = {
      id: `s-${Date.now()}`,
      title: 'New Section',
      content: ''
    };
    setSections([...sections, newSection]);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text(title, 20, 20);
    
    doc.setFontSize(10);
    doc.text(`Owner: ${owner} | Approver: ${approver} | Version: ${policy.version}`, 20, 30);
    
    let y = 40;
    sections.forEach(section => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(section.title, 20, y);
      y += 10;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const splitText = doc.splitTextToSize(section.content, 170);
      doc.text(splitText, 20, y);
      y += (splitText.length * 7) + 10;
    });
    
    doc.save(`${title.replace(/\s+/g, '_')}_v${policy.version}.pdf`);
  };

  const handleSave = () => {
    onSave({
      ...policy,
      title,
      owner,
      approver,
      nextReviewDate,
      sections,
      coveredControls,
      lastUpdated: new Date().toISOString()
    });
  };

  const handleRestore = (version: PolicyVersion) => {
    if (confirm(`Restore version ${version.version}? Current unsaved changes will be lost.`)) {
      setSections(version.sections);
      setActiveTab('editor');
    }
  };

  const handleReevaluate = async () => {
    setAnalyzing(true);
    try {
      const content = sections.map(s => `${s.title}\n${s.content}`).join('\n\n');
      
      // Build framework context for multi-framework AI analysis
      let frameworkContext = undefined;
      if (framework && frameworkControls && frameworkControls.length > 0) {
        frameworkContext = {
          code: framework.code,
          name: framework.name,
          frameworkType: framework.framework_type,
          controls: frameworkControls.map(c => ({
            code: c.control_code || `ctrl-${c.id}`,
            title: c.title,
            description: c.description || undefined
          }))
        };
      }
      
      const controls = await aiService.analyzePolicyControls(content, frameworkContext);
      setCoveredControls(controls);
    } catch (error) {
      console.error(error);
      alert('Failed to analyze policy');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleControlClick = (controlId?: string) => {
    setSelectedControlId(controlId || null);
    setShowControlsModal(true);
  };

  const filteredControls = useMemo(() => {
    let controls = soaControls.filter(c => coveredControls.includes(c.id));
    if (selectedControlId) {
      controls = controls.filter(c => c.id === selectedControlId);
    }
    return controls;
  }, [soaControls, coveredControls, selectedControlId]);

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={onCancel} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-2xl font-bold bg-transparent text-gray-900 dark:text-gray-100 border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-indigo-500 focus:outline-none px-2"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handleExportPDF} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600">
              <Download className="w-4 h-4" /> Export PDF
            </button>
            <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
              <Save className="w-4 h-4" /> Save Changes
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('editor')}
            className={`pb-2 px-1 flex items-center gap-2 ${activeTab === 'editor' ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          >
            <FileText className="w-4 h-4" /> Editor
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-2 px-1 flex items-center gap-2 ${activeTab === 'history' ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          >
            <History className="w-4 h-4" /> Version History
          </button>
        </div>

        {activeTab === 'editor' ? (
          <>
            <div className="grid grid-cols-2 gap-4 mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Owner</label>
                <input 
                  type="text" 
                  value={owner} 
                  onChange={e => setOwner(e.target.value)} 
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g. CISO"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Approver</label>
                <input 
                  type="text" 
                  value={approver} 
                  onChange={e => setApprover(e.target.value)} 
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g. Board of Directors"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Next Review Date</label>
                <input 
                  type="date" 
                  value={nextReviewDate} 
                  onChange={e => setNextReviewDate(e.target.value)} 
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="col-span-2">
                <div className="flex justify-between items-center mb-1">
                  <label 
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400"
                    onClick={() => handleControlClick()}
                  >
                    Covered {framework?.framework_type === 'law' || framework?.framework_type === 'regulation' 
                      ? 'Requirements/Articles' 
                      : 'Controls'} 
                    {framework ? ` (${framework.code})` : ''} 
                    <span className="text-xs ml-1">(Click to view details)</span>
                  </label>
                  <button 
                    onClick={handleReevaluate} 
                    disabled={analyzing}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center gap-1 disabled:opacity-50"
                  >
                    {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Re-evaluate with AI
                  </button>
                </div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {coveredControls.map(c => (
                    <span 
                      key={c} 
                      onClick={() => handleControlClick(c)}
                      className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded text-xs font-medium border border-indigo-100 dark:border-indigo-800 cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                    >
                      {c}
                    </span>
                  ))}
                  {(!coveredControls || coveredControls.length === 0) && (
                    <span className="text-sm text-gray-500 dark:text-gray-400 italic">No specific controls linked.</span>
                  )}
                </div>
              </div>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-4">
                  {sections.map((section) => (
                    <SortableItem
                      key={section.id}
                      id={section.id}
                      section={section}
                      onUpdate={updateSection}
                      onDelete={deleteSection}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <button onClick={addSection} className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-indigo-500 dark:hover:border-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-400 flex items-center justify-center gap-2">
              <Plus className="w-5 h-5" /> Add Section
            </button>
          </>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Changed By</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {policy.history && policy.history.length > 0 ? (
                  policy.history.map((version, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{version.version}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(version.date).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{version.changedBy}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => setViewVersion(version)}
                            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" /> View
                          </button>
                          <button 
                            onClick={() => handleRestore(version)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 flex items-center gap-1"
                          >
                            <RotateCcw className="w-4 h-4" /> Restore
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">No history available.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Version View Modal */}
      <Modal isOpen={!!viewVersion} onClose={() => setViewVersion(null)} title={`Version ${viewVersion?.version} - ${viewVersion?.date ? new Date(viewVersion.date).toLocaleString() : ''}`}>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto p-1">
          {viewVersion?.sections.map((section, idx) => (
            <div key={idx} className="border-b dark:border-gray-700 pb-4 last:border-0">
              <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-2">{section.title}</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{section.content}</p>
            </div>
          ))}
        </div>
      </Modal>

      {/* Controls View Modal */}
      <Modal 
        isOpen={showControlsModal} 
        onClose={() => setShowControlsModal(false)} 
        title={selectedControlId 
          ? `${framework?.framework_type === 'law' || framework?.framework_type === 'regulation' ? 'Requirement' : 'Control'} Details: ${selectedControlId}` 
          : `Covered ${framework?.framework_type === 'law' || framework?.framework_type === 'regulation' ? 'Requirements' : 'Controls'}${framework ? ` (${framework.code})` : ''}`
        }
      >
        <div className="flex justify-end mb-2">
          {onNavigate && (
            <button
              onClick={() => {
                if (selectedControlId) {
                  onNavigate('soa', { filterIds: [selectedControlId] });
                } else {
                  onNavigate('soa', { filterIds: coveredControls });
                }
              }}
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center gap-1"
            >
              <Eye className="w-4 h-4" /> View in SoA Editor
            </button>
          )}
        </div>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto p-1">
          {filteredControls.length > 0 ? (
            filteredControls.map(control => (
              <div key={control.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-gray-900 dark:text-gray-100">{control.id} - {control.name}</h4>
                  {control.applicable ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3" /> Applicable
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full">
                      <XCircle className="w-3 h-3" /> N/A
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{control.description}</p>
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p><span className="font-medium">Justification:</span> {control.justification || 'N/A'}</p>
                  <p><span className="font-medium">Status:</span> {control.implementationStatus}</p>
                  <p><span className="font-medium">Method:</span> {control.implementationMethod || 'N/A'}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">No controls found.</p>
          )}
        </div>
      </Modal>
    </div>
  );
};
